import React, { useState, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, AuthContext } from '@/App';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Scan, User, Package, ArrowRight, Plus, Loader2 } from 'lucide-react';

export function ScannerProvider({ children }) {
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [scanResult, setScanResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleScan = useCallback(async (code) => {
    if (!code || code.length < 3) return;
    
    console.log('Scanned code:', code);
    
    // If not logged in, try QR login
    if (!user) {
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/auth/qr-login`, { matricule: code });
        login(data.token, data.user);
        toast.success(`Bienvenue, ${data.user.name}!`);
      } catch (error) {
        toast.error('Code non reconnu');
      } finally {
        setLoading(false);
      }
      return;
    }

    // If logged in, search for tool or collaborator
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/scan/${encodeURIComponent(code)}`);
      
      if (data.match_type === 'not_found') {
        toast.error(`Code "${code}" non trouvé`, {
          description: 'Aucun outil ou collaborateur correspondant'
        });
        return;
      }

      setScanResult(data);
      setDialogOpen(true);
      
    } catch (error) {
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  }, [user, login]);

  // Enable scanner everywhere except in input fields
  useBarcodeScanner({
    onScan: handleScan,
    enabled: true,
    minLength: 3,
    maxDelay: 100 // Increase delay for slower scanners
  });

  const handleNavigateToCollaborator = () => {
    if (scanResult?.collaborator) {
      navigate(`/collaborators/${scanResult.collaborator.id}`);
      setDialogOpen(false);
      setScanResult(null);
    }
  };

  const handleNavigateToTool = () => {
    if (scanResult?.tool) {
      navigate(`/tools/${scanResult.tool.id}`);
      setDialogOpen(false);
      setScanResult(null);
    }
  };

  const handleQuickAssign = () => {
    if (scanResult?.tool) {
      // If already on a collaborator detail page, we can trigger assignment
      const match = location.pathname.match(/\/collaborators\/([^/]+)/);
      if (match) {
        // Dispatch custom event for the collaborator page to handle
        window.dispatchEvent(new CustomEvent('quick-assign-tool', {
          detail: { tool: scanResult.tool }
        }));
        setDialogOpen(false);
        setScanResult(null);
        return;
      }
      // Otherwise navigate to assignments with the tool pre-selected
      navigate('/assignments', { state: { preselectedTool: scanResult.tool } });
      setDialogOpen(false);
      setScanResult(null);
    }
  };

  const getMatchTypeLabel = (type) => {
    switch (type) {
      case 'matricule': return 'Matricule';
      case 'barcode': return 'Code-barres';
      case 'article_number': return 'N° Article';
      case 'article_number_partial': return 'N° Article (partiel)';
      default: return type;
    }
  };

  return (
    <>
      {children}
      
      {/* Loading indicator for scanner */}
      {loading && (
        <div className="fixed top-4 right-4 z-50 bg-primary text-primary-foreground px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm font-medium">Recherche...</span>
        </div>
      )}

      {/* Scan Result Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="h-5 w-5" />
              Code scanné
            </DialogTitle>
            <DialogDescription>
              Code détecté : <Badge variant="secondary" className="font-mono">{scanResult?.code}</Badge>
              <span className="ml-2 text-xs">({getMatchTypeLabel(scanResult?.match_type)})</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Collaborator Result */}
            {scanResult?.collaborator && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-full">
                      <User className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{scanResult.collaborator.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Matricule: {scanResult.collaborator.matricule}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tool Result */}
            {scanResult?.tool && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <Package className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{scanResult.tool.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {scanResult.tool.article_number}
                        {scanResult.tool.barcode && ` • ${scanResult.tool.barcode}`}
                      </p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          Stock: {scanResult.tool.quantity_available}/{scanResult.tool.quantity_total}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {scanResult.tool.type}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {scanResult?.collaborator && (
              <Button onClick={handleNavigateToCollaborator} className="w-full sm:w-auto">
                <ArrowRight className="h-4 w-4 mr-2" />
                Voir la fiche
              </Button>
            )}
            {scanResult?.tool && (
              <>
                <Button onClick={handleNavigateToTool} variant="outline" className="w-full sm:w-auto">
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Voir l'outil
                </Button>
                {user?.role === 'admin' && scanResult.tool.quantity_available > 0 && (
                  <Button onClick={handleQuickAssign} className="w-full sm:w-auto">
                    <Plus className="h-4 w-4 mr-2" />
                    Affecter
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ScannerProvider;
